import { useZigzag } from '@common/device-manager';
import { useTracker, useUBL, ZigzagEvent } from '@common/log-manager';
import { logTTIMetrics } from '@common/metrics';

import { PdpBrowsingType } from '../graphql/catalog-product-browsing-type_types.2';

const getUrl = () => {
  return typeof window !== 'undefined' ? window.location.href : null;
};

export const usePdpUBL = () => {
  const is_app = useZigzag();
  const sendUBL = useUBL();
  const tracker = useTracker();

  const pageviewUBL = (catalog_product_id: string, browsingType: PdpBrowsingType) => {
    tracker.sendImmediately({
      category: 'pageview',
      navigation: 'product_detail',
      navigation_sub: {
        page_url: getUrl(),
      },
      data: {
        browsing_type: browsingType,
        catalog_product_id,
        is_opened: true,
      },
    });
  };

  const appDownloadUBL = async (catalog_product_id: string) => {
    await tracker.sendImmediately({
      category: 'click',
      navigation: 'product_detail',
      navigation_sub: {
        page_url: getUrl(),
      },
      object_type: 'button',
      object_id: 'app_download',
      data: { catalog_product_id, is_opened: true },
    });
  };

  const homeClickUBL = async (catalog_product_id: string) => {
    await tracker.sendImmediately({
      category: 'click',
      navigation: 'product_detail',
      navigation_sub: {
        page_url: getUrl(),
      },
      object_section: 'top_bar',
      object_type: 'button',
      object_id: 'zigzag_home',
      data: { catalog_product_id },
    });
  };

  const myPageClickUBL = async (catalog_product_id: string) => {
    await tracker.sendImmediately({
      category: 'click',
      navigation: 'product_detail',
      navigation_sub: {
        page_url: getUrl(),
      },
      object_section: 'top_bar',
      object_type: 'button',
      object_id: 'mypage',
      data: { catalog_product_id },
    });
  };

  const searchClickUBL = async (catalog_product_id: string) => {
    await tracker.sendImmediately({
      category: 'click',
      navigation: 'product_detail',
      navigation_sub: {
        page_url: getUrl(),
      },
      object_section: 'top_bar',
      object_type: 'button',
      object_id: 'search',
      data: { catalog_product_id },
    });
  };

  const cartClickUBL = async (catalog_product_id: string) => {
    await tracker.sendImmediately({
      category: 'click',
      navigation: 'product_detail',
      navigation_sub: {
        page_url: getUrl(),
      },
      object_section: 'top_bar',
      object_type: 'button',
      object_id: 'cart',
      data: { catalog_product_id },
    });
  };

  const discountInfoClickUBL = (catalog_product_id: string) => {
    tracker.addLog({
      category: 'click',
      navigation: 'product_detail',
      navigation_sub: {
        page_url: getUrl(),
      },
      object_type: 'button',
      object_id: 'discount_info',
      data: { catalog_product_id },
    });
  };

  const shippingInfoClickUBL = (catalog_product_id: string) => {
    tracker.addLog({
      category: 'click',
      navigation: 'product_detail',
      navigation_sub: {
        page_url: getUrl(),
      },
      object_type: 'button',
      object_id: 'shipping_info',
      data: { catalog_product_id },
    });
  };

  const couponClickUBL = (catalog_product_id: string) => {
    tracker.addLog({
      category: 'click',
      navigation: 'product_detail',
      navigation_sub: {
        page_url: getUrl(),
      },
      object_type: 'button',
      object_id: 'seller_coupon',
      data: { catalog_product_id },
    });
  };

  const storeHomeClickUBL = async (catalog_product_id: string, shop_id: string) => {
    await tracker.sendImmediately({
      category: 'click',
      navigation: 'product_detail',
      navigation_sub: {
        page_url: getUrl(),
      },
      object_section: 'store_info',
      object_type: 'button',
      object_id: shop_id,
      data: { catalog_product_id },
    });
  };

  const sizeInfoTabClickUBL = (catalog_product_id: string) => {
    tracker.addLog({
      category: 'click',
      navigation: 'product_detail',
      navigation_sub: {
        page_url: getUrl(),
      },
      object_type: 'button',
      object_id: 'subtab_size',
      data: { catalog_product_id },
    });
  };

  const sizeInfoTabImpressionUBL = async (catalog_product_id: string) => {
    const params = {
      category: 'impression' as const,
      navigation: 'product_detail',
      object_type: 'button',
      object_id: 'subtab_size',
    };

    if (is_app) {
      ZigzagEvent.subl({
        ...params,
        data: JSON.stringify({
          catalog_product_id,
        }),
      });
      return;
    }

    await tracker.sendImmediately({
      ...params,
      navigation_sub: {
        page_url: getUrl(),
      },
      data: { catalog_product_id },
    });
  };

  const reviewTabClickUBL = (
    catalog_product_id: string,
    predictSummary?: { hasPredictSummary: boolean; rating: boolean; satisfaction: boolean },
  ) => {
    tracker.addLog({
      category: 'click',
      navigation: 'product_detail',
      navigation_sub: {
        page_url: getUrl(),
      },
      object_section: 'subtab',
      object_type: 'button',
      object_id: 'subtab_review',
      data: {
        catalog_product_id,
        review_predict_summary: predictSummary?.hasPredictSummary || false,
        rating: predictSummary?.rating || false,
        satisfaction: predictSummary?.satisfaction || false,
      },
    });
  };

  const recommendationTabClickUBL = (catalog_product_id: string) => {
    tracker.addLog({
      category: 'click',
      navigation: 'product_detail',
      navigation_sub: {
        page_url: getUrl(),
      },
      object_section: 'subtab',
      object_type: 'button',
      object_id: 'subtab_recommendation',
      data: { catalog_product_id },
    });
  };

  const seeMoreClickUBL = (catalog_product_id: string) => {
    tracker.addLog({
      category: 'click',
      navigation: 'product_detail',
      navigation_sub: {
        page_url: getUrl(),
      },
      object_section: 'subtab_description',
      object_type: 'button',
      object_id: 'see_more',
      data: { catalog_product_id },
    });
  };

  const eventBannerImpressionUBL = async (banner_id: string, index: number, catalog_product_id: string) => {
    await tracker.sendImmediately({
      category: 'impression',
      navigation: 'product_detail',
      navigation_sub: {
        page_url: getUrl(),
      },
      object_section: 'event_banner',
      object_type: 'banner',
      object_id: banner_id,
      object_idx: index,
      data: { catalog_product_id },
    });
  };

  const eventBannerClickUBL = async (banner_id: string, index: number, catalog_product_id: string) => {
    await tracker.sendImmediately({
      category: 'click',
      navigation: 'product_detail',
      navigation_sub: {
        page_url: getUrl(),
      },
      object_section: 'event_banner',
      object_type: 'banner',
      object_id: banner_id,
      object_idx: index,
      data: { catalog_product_id },
    });
  };

  const checkoutClickUBL = (catalog_product_id: string) => {
    tracker.addLog({
      category: 'click',
      navigation: 'product_detail',
      navigation_sub: {
        page_url: getUrl(),
      },
      object_section: 'bottom_bar',
      object_type: 'button',
      object_id: 'checkout',
      data: { catalog_product_id },
    });
  };

  const subtabReviewTopClickUBL = () => {
    tracker.sendImmediately({
      category: 'click',
      navigation: 'product_detail',
      navigation_sub: {
        page_url: getUrl(),
      },
      object_section: 'subtab_review_top',
      object_type: 'button',
      object_id: 'app_download',
    });
  };

  const subtabReviewBottomClickUBL = () => {
    tracker.sendImmediately({
      category: 'click',
      navigation: 'product_detail',
      navigation_sub: {
        page_url: getUrl(),
      },
      object_section: 'subtab_review_bottom',
      object_type: 'button',
      object_id: 'app_download',
    });
  };

  const subtabInquiryTopClickUBL = () => {
    tracker.sendImmediately({
      category: 'click',
      navigation: 'product_detail',
      navigation_sub: {
        page_url: getUrl(),
      },
      object_section: 'subtab_inquiry_top',
      object_type: 'button',
      object_id: 'app_download',
    });
  };

  const subtabInquiryBottomClickUBL = () => {
    tracker.sendImmediately({
      category: 'click',
      navigation: 'product_detail',
      navigation_sub: {
        page_url: getUrl(),
      },
      object_section: 'subtab_inquiry_bottom',
      object_type: 'button',
      object_id: 'app_download',
    });
  };

  const ttiUBL = (catalog_product_id: string) => {
    const navigation = 'product_detail';
    const log = {
      category: 'tti' as const,
      navigation,
      data: {
        tti: {
          category: 'scene_tti',
          label: 'web_end',
        },
      },
    };

    /**
     * legacy
     * TODO: 추후 제거
     */
    ZigzagEvent.subl({
      category: 'tti',
      navigation,
      data: JSON.stringify({
        catalog_product_id,
        tti: {
          category: 'scene_tti',
          label: 'initial_render_end',
        },
      }),
    });

    logTTIMetrics({
      payload: log,
      sender: sendUBL,
    });
  };

  return {
    pageviewUBL,
    appDownloadUBL,
    homeClickUBL,
    myPageClickUBL,
    searchClickUBL,
    cartClickUBL,
    discountInfoClickUBL,
    shippingInfoClickUBL,
    couponClickUBL,
    storeHomeClickUBL,
    sizeInfoTabClickUBL,
    sizeInfoTabImpressionUBL,
    reviewTabClickUBL,
    recommendationTabClickUBL,
    seeMoreClickUBL,
    eventBannerImpressionUBL,
    eventBannerClickUBL,
    checkoutClickUBL,
    subtabReviewTopClickUBL,
    subtabReviewBottomClickUBL,
    subtabInquiryTopClickUBL,
    subtabInquiryBottomClickUBL,
    ttiUBL,
  };
};
